// @flow

import * as React from 'react';
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay';
import { Map, useMap } from 'rgm';
import { useSystem } from 'react-system';
import { useGoogleApi, fitBounds } from '@realadvisor/google-maps';
import { Dialog, Tabs, Tab, ButtonBase } from '@material-ui/core';
import { useResizeRect } from '@realadvisor/observe';
import { useTheme } from '../../theme';
import { Close } from '../../icons/close';
import { Fullscreen } from '../../icons/fullscreen';
import { useLocale } from '../../hooks/locale';
import { useHasBeenVisible } from '../../hooks/visibility-observer';
import { polyline } from '../../utils/polyline';
import type { aggregatesListingMap_listing$key } from './__generated__/aggregatesListingMap_listing.graphql';
import type { aggregatesListingMapQuery } from './__generated__/aggregatesListingMapQuery.graphql';

const MapPolygon = ({ coordinates }) => {
  const { map } = useMap();

  React.useEffect(() => {
    const object = {
      type: 'Feature',
      geometry: { type: 'MultiPolygon', coordinates },
    };

    let style = {};
    const tmpListener = map.data.addListener('addfeature', event => {
      map.data.overrideStyle(event.feature, style);
    });

    const features = [];

    style = {
      fillColor: 'transparent',
      strokeColor: 'white',
      strokeWeight: 4,
      strokeOpacity: 1,
      cursor: 'hand',
    };
    features.push(...map.data.addGeoJson(object));

    style = {
      fillColor: 'transparent',
      strokeColor: 'blue',
      strokeWeight: 2,
      strokeOpacity: 1,
      cursor: 'hand',
    };
    features.push(...map.data.addGeoJson(object));

    tmpListener.remove();

    return () => {
      features.forEach(feature => map.data.remove(feature));
    };
  }, [map, coordinates]);

  return null;
};

const MapMarker = ({ position }) => {
  const { api, map } = useMap();

  React.useEffect(() => {
    const marker = new api.Marker({ position, map });
    return () => {
      marker.setMap(null);
    };
  }, [map, api, position]);

  return null;
};

const MapUpdateType = ({ mapTypeId }) => {
  const { map } = useMap();

  React.useEffect(() => {
    map.setMapTypeId(mapTypeId);
  }, [map, mapTypeId]);

  return null;
};

const bboxToOptions = (bbox, size) => {
  if (bbox == null) {
    return null;
  }
  const [w, n, e, s] = bbox;
  if (w == null || n == null || e == null || s == null) {
    return null;
  }
  const result = fitBounds(
    {
      sw: { lat: s, lng: w },
      ne: { lat: n, lng: e },
    },
    size,
    20,
  );
  if (result == null) {
    return null;
  }
  return { center: result.center, zoom: result.zoom };
};

const MapWithData = ({ slug, tab, containerRef, mapApi }) => {
  const data = useLazyLoadQuery<aggregatesListingMapQuery>(
    graphql`
      query aggregatesListingMapQuery($slug: String!) {
        aggregatesListingBySlug(slug: $slug) {
          mapData {
            point {
              lat
              lng
            }
            area(lowRes: false) {
              bbox
              coordinates
            }
          }
        }
      }
    `,
    { slug },
  );

  const containerRect = useResizeRect(containerRef);
  const mapData = data.aggregatesListingBySlug?.mapData;

  const initialMapType = 'roadmap';
  const currentMapType = tab === 'satellite' ? 'satellite' : 'roadmap';

  const commonOptions = {
    clickableIcons: false,
    mapTypeControl: false,
    gestureHandling: 'greedy',

    // If we use currentMapType here,
    // the map will reload every time user changes type (bad UX)
    mapTypeId: initialMapType,
  };

  if (containerRect == null) {
    return null;
  }

  const size = {
    width: containerRect.width,
    height: containerRect.height,
  };

  if (mapData?.point != null) {
    const position = mapData.point;
    return (
      <div css={{ position: 'absolute', ...size }}>
        <Map
          api={mapApi}
          options={{ zoom: 15, center: position, ...commonOptions }}
        >
          <MapMarker position={position} />
          <MapUpdateType mapTypeId={currentMapType} />
        </Map>
      </div>
    );
  }

  if (mapData?.area != null) {
    const area = mapData.area;
    const boundsOptions = bboxToOptions(area.bbox, size);
    if (boundsOptions) {
      return (
        <div css={{ position: 'absolute', ...size }}>
          <Map api={mapApi} options={{ ...boundsOptions, ...commonOptions }}>
            <MapPolygon coordinates={area.coordinates} />
            <MapUpdateType mapTypeId={currentMapType} />
          </Map>
        </div>
      );
    }
  }

  return null;
};

const DialogContent = ({ slug, mapApi, onClose }) => {
  const { t } = useLocale();
  const containerRef = React.useRef();
  const [tab, setTab] = React.useState('map');

  // We set background to the one Google uses to make loading states less messy
  const GOOGLE_MAPS_BACKGROUD = '#e5e3df';

  return (
    <div css={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div css={{ display: 'flex', justifyContent: 'space-between' }}>
        <Tabs
          onChange={(e, val) => setTab(val)}
          value={tab}
          indicatorColor="primary"
        >
          <Tab
            label={t('map')}
            value="map"
            css={tab === 'map' ? null : { cursor: 'pointer' }}
          />
          <Tab
            label={t('satellite')}
            value="satellite"
            css={tab === 'satellite' ? null : { cursor: 'pointer' }}
          />
        </Tabs>
        <ButtonBase onClick={onClose} css={{ padding: '0 14px' }}>
          <Close size={24} />
        </ButtonBase>
      </div>
      <div
        css={{
          flexGrow: 1,
          background: GOOGLE_MAPS_BACKGROUD,
          position: 'relative',
        }}
        ref={containerRef}
      >
        <React.Suspense fallback={null}>
          <MapWithData
            slug={slug}
            tab={tab}
            containerRef={containerRef}
            mapApi={mapApi}
          />
        </React.Suspense>
      </div>
    </div>
  );
};

const DynamicMapDialog = ({ open, onClose, slug }) => {
  const { media } = useSystem();
  const mapApi = useGoogleApi(open);

  const MARGIN_DESKTOP = 32;
  const MARGIN_MOBILE = 16;
  const fullSize = [
    `calc(100% - ${MARGIN_MOBILE * 2}px)`,
    `calc(100% - ${MARGIN_DESKTOP * 2}px)`,
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      css={{
        '.MuiDialog-paper': media({
          margin: [MARGIN_MOBILE, MARGIN_DESKTOP],
          height: fullSize,
          width: fullSize,
          maxWidth: '1080px',
          maxHeight: '720px',
          borderRadius: '4px',
        }),
      }}
    >
      {mapApi && (
        <DialogContent slug={slug} mapApi={mapApi} onClose={onClose} />
      )}
    </Dialog>
  );
};

const StaticMap = ({ data, slug }) => {
  const { colors } = useTheme();

  const containerRef = React.useRef();
  const containerRect = useResizeRect(containerRef);
  const containerHasBeenVisible = useHasBeenVisible(containerRef);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const size =
    containerRect == null
      ? null
      : {
          width: Math.round(containerRect.width),
          height: Math.round((containerRect.width * 11) / 20),
        };

  const params = [];

  if (size != null) {
    params.unshift(['size', `${size.width}x${size.height}@2x`]);
  }

  if (data.type === 'point') {
    params.unshift(
      ['zoom', 15],
      ['markers', `${data.value.lat},${data.value.lng},,red`],
    );
  }

  if (data.type === 'area') {
    params.unshift(
      ...data.value.coordinates.map(singlePolygon => {
        // https://geojson.org/geojson-spec.html#polygon
        const [exteriorRing /*, ...holes*/] = singlePolygon;
        const points = exteriorRing.map(([lng, lat]) => [lat, lng]);
        return ['lines', `${polyline.encode(points, 6)},blue,4`];
      }),
    );
  }

  const searchString = params
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');

  return (
    <>
      <div
        ref={containerRef}
        onClick={() => setDialogOpen(true)}
        css={{ cursor: 'pointer', position: 'relative' }}
      >
        {size &&
          (containerHasBeenVisible ? (
            <img
              css={{ ...size, borderRadius: 4 }}
              src={`https://tiles.stadiamaps.com/static/outdoors.png?${searchString}`}
            />
          ) : (
            <div css={size} />
          ))}
        <div
          css={{
            position: 'absolute',
            top: 10,
            right: 10,
            padding: 2,
            background: 'white',
            borderRadius: 2,
          }}
        >
          <Fullscreen fill={colors.grey600} size={32} />
        </div>
      </div>

      <DynamicMapDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        slug={slug}
      />
    </>
  );
};

export const AggregatesListingMap = (props: {|
  listing: aggregatesListingMap_listing$key,
|}): React.Node => {
  const { mapData, slug } = useFragment(
    graphql`
      fragment aggregatesListingMap_listing on AggregatesListing {
        slug
        mapData {
          point {
            lat
            lng
          }
          area(lowRes: true) {
            coordinates
          }
        }
      }
    `,
    props.listing,
  );

  if (mapData?.point != null) {
    return (
      <StaticMap data={{ type: 'point', value: mapData.point }} slug={slug} />
    );
  }

  if (mapData?.area != null) {
    return (
      <StaticMap data={{ type: 'area', value: mapData.area }} slug={slug} />
    );
  }

  return null;
};
